import React, { useEffect, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

export default function BookingViewPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [masters, setMasters] = useState({
    clients: [],
    resources: [],
    editions: [],
    positions: [],
    statuses: [],
    users: []
  });

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        setLoading(true);
        const [bookingRes, auditRes, clientsRes, resourcesRes, editionsRes, positionsRes, statusesRes, usersRes] = await Promise.all([
          api.get(`/bookings/${bookingId}`),
          api.get(`/bookings/${bookingId}/audit`),
          api.get('/master/clients'),
          api.get('/master/media-resources'),
          api.get('/master/editions'),
          api.get('/master/positions'),
          api.get('/statuses'),
          api.get('/users')
        ]);
        setBooking(bookingRes.data.booking);
        setAuditLogs(auditRes.data.logs || []);
        setMasters({
          clients: clientsRes.data.clients || [],
          resources: resourcesRes.data.resources || [],
          editions: editionsRes.data.editions || [],
          positions: positionsRes.data.positions || [],
          statuses: statusesRes.data.statuses || [],
          users: usersRes.data.users || []
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId]);

  const formatAuditValue = (val) => {
    if (!val) return '-';
    try {
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      
      // Define a map of internal keys to user-friendly labels
      const keyLabels = {
        client_id: 'Client',
        media_resource_id: 'Publication',
        edition_id: 'Edition',
        position_id: 'Position',
        media_type: 'Media Type',
        platform_details: 'Platform Details',
        time_from: 'Time From',
        time_to: 'Time To',
        material_due_date: 'Material Due',
        notes: 'Notes',
        status_id: 'Status',
        status_name: 'Status',
        owner_user_id: 'Owner',
        booking_source: 'Source',
        new_client_name: 'New Client Name',
        releaseDates: 'Release Dates',
        clientName: 'Client',
        mediaName: 'Publication',
        editionName: 'Edition',
        positionName: 'Position',
        ownerName: 'Owner'
      };

      // Filter out internal/technical fields that aren't useful for the user
      const ignoredKeys = ['id', 'created_at', 'updated_at', 'created_by', 'is_archived', 'released_at', 'is_final', 'is_double_bookable', 'is_locked'];

      // Fields that have both an ID and a Name version in the same object
      // We prefer the Name version if available, otherwise we'll look up the ID
      const redundantIdKeys = ['client_id', 'media_resource_id', 'edition_id', 'position_id', 'status_id', 'owner_user_id'];

      const entries = Object.entries(parsed)
        .filter(([key]) => {
          // Ignore technical keys
          if (ignoredKeys.includes(key)) return false;
          
          // If it's an ID key (e.g., client_id), check if the name key (e.g., clientName) is also present
          if (key === 'client_id' && parsed.clientName) return false;
          if (key === 'media_resource_id' && parsed.mediaName) return false;
          if (key === 'edition_id' && parsed.editionName) return false;
          if (key === 'position_id' && parsed.positionName) return false;
          if (key === 'status_id' && parsed.status_name) return false;
          if (key === 'owner_user_id' && parsed.ownerName) return false;
          
          return true;
        })
        .map(([key, value]) => {
          const label = keyLabels[key] || key;
          let displayValue = value;

          if (value === null || value === undefined || value === '') {
            displayValue = '-';
          } else if (key === 'releaseDates' && Array.isArray(value)) {
            displayValue = value.map(rd => formatDate(rd.release_date || rd.date)).join(', ');
          } else if (redundantIdKeys.includes(key)) {
            // Look up name for IDs if we don't have the name field
            const id = parseInt(value);
            if (key === 'client_id') displayValue = masters.clients.find(c => c.id === id)?.name || value;
            else if (key === 'media_resource_id') displayValue = masters.resources.find(r => r.id === id)?.name || value;
            else if (key === 'edition_id') displayValue = masters.editions.find(e => e.id === id)?.name || value;
            else if (key === 'position_id') displayValue = masters.positions.find(p => p.id === id)?.name || value;
            else if (key === 'status_id') displayValue = masters.statuses.find(s => s.id === id)?.name || value;
            else if (key === 'owner_user_id') displayValue = masters.users.find(u => u.id === id)?.name || value;
          } else if (typeof value === 'object') {
            displayValue = JSON.stringify(value);
          }

          return (
            <div key={key} style={{ marginBottom: '4px', fontSize: '0.85rem' }}>
              <span style={{ fontWeight: 700, color: '#334155', minWidth: '100px', display: 'inline-block' }}>{label}:</span> 
              <span style={{ color: '#1e293b' }}>{String(displayValue)}</span>
            </div>
          );
        });

      return entries.length > 0 ? entries : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No changes to display</span>;
    } catch (e) {
      return String(val);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <p>Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="card">
          <p>Booking not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="booking-details-card">
        <div className="details-header">
          <div>
            <strong style={{ fontSize: '1.1rem' }}>Booking Details</strong>
            <p style={{ margin: 0, color: '#64748b' }}>{booking.booking_source} booking</p>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
           
            <button className="btn-primary" onClick={() => navigate('/bookings')}>
              Back
            </button>
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <div className="details-grid">
            <div className="form-field">
              <label>Client</label>
              <p>{booking.clientName || booking.new_client_name || '-'}</p>
            </div>
            <div className="form-field">
              <label>Media Resource</label>
              <p>{booking.mediaName || '-'}</p>
            </div>
            <div className="form-field">
              <label>Edition</label>
              <p>{booking.editionName || '-'}</p>
            </div>
            <div className="form-field">
              <label>Position</label>
              <p>{booking.positionName || '-'}</p>
            </div>
            <div className="form-field">
              <label>Media Type</label>
              <p>{booking.media_type || '-'}</p>
            </div>
            <div className="form-field">
              <label>Material Due</label>
              <p>{formatDate(booking.material_due_date)}</p>
            </div>
            <div className="form-field">
              <label>Status</label>
              <p>
                {booking.booking_source === 'PUBLICATION' ? (
                  '-'
                ) : booking.notes && booking.notes.includes('[Queue Position: Booking') ? (
                  `QUEUED: ${booking.notes.match(/Booking \d+/)[0]}`
                ) : (
                  booking.status_name || '-'
                )}
              </p>
            </div>
            <div className="form-field">
              <label>Owner</label>
              <p>{booking.ownerName || '-'}</p>
            </div>
            <div className="form-field">
              <label>Platform Details</label>
              <p>{booking.platform_details || '-'}</p>
            </div>
            <div className="form-field">
              <label>Time From / To</label>
              <p>
                {booking.time_from || '-'} / {booking.time_to || '-'}
              </p>
            </div>
            <div className="form-field">
              <label>Notes</label>
              <p>{booking.notes || '-'}</p>
            </div>
          </div>
        </div>
        <div style={{ marginTop: '2rem' }} className="release-table">
          <div style={{ padding: '1rem 1.2rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
            Release Dates
          </div>
          <div className="scroll-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Locked</th>
                </tr>
              </thead>
              <tbody>
                {booking.releaseDates?.length ? (
                  booking.releaseDates.map((release) => (
                    <tr key={release.id}>
                      <td>{formatDate(release.release_date)}</td>
                      <td>{release.status_name || '-'}</td>
                      <td>{release.is_locked ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">No release dates.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop: '2rem' }} className="release-table">
          <div style={{ padding: '1rem 1.2rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
            Audit Trail
          </div>
          <div className="scroll-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Date</th>
                  <th style={{ width: '150px' }}>User</th>
                  <th style={{ width: '150px' }}>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length ? (
                  auditLogs.map((log) => (
                    <Fragment key={log.id}>
                      <tr 
                        onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                        style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.changed_at).toLocaleString()}</td>
                        <td>{log.userName || 'System'}</td>
                        <td>
                          <span className={`tag-pill ${log.action.toLowerCase()}`}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ color: '#3b82f6', fontSize: '0.85rem', fontWeight: 500 }}>
                          {expandedLogId === log.id ? 'Click to hide changes ↑' : 'Click to view changes ↓'}
                        </td>
                      </tr>
                      {expandedLogId === log.id && (
                        <tr>
                          <td colSpan="4" style={{ padding: '1.5rem', background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                              <div>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                                  Before
                                </div>
                                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '100px' }}>
                                  {log.old_value ? formatAuditValue(log.old_value) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No previous data</span>}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                                  After
                                </div>
                                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '100px' }}>
                                  {log.new_value ? formatAuditValue(log.new_value) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No new data</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No audit logs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

